import * as THREE from 'three'
import bodyVertex from '@/shaders/body.vert.glsl?raw'
import planetFragment from '@/shaders/planet.frag.glsl?raw'
import glowVertex from '@/shaders/glow.vert.glsl?raw'
import glowFragment from '@/shaders/glow.frag.glsl?raw'
import ringVertex from '@/shaders/ring.vert.glsl?raw'
import ringFragment from '@/shaders/ring.frag.glsl?raw'
import { withNoise } from '@/_utilities/shader'
import { damp } from '@/_utilities/math'
import { sceneConfig } from '@/_constants/scene-config'
import type { CelestialBody } from '@/_types/celestial-body'

/**
 * A single orbiting service planet. Owns its orbital motion, self-rotation, the
 * procedural surface material, an atmosphere glow shell, an optional ring, and an
 * optional asteroid cluster. Exposes a pick mesh for raycasting and a highlight
 * control for hover feedback.
 */
export class Planet {
  public readonly object: THREE.Group
  public readonly pickMesh: THREE.Mesh
  public readonly body: CelestialBody

  private readonly pivot: THREE.Group
  private readonly bodyGroup: THREE.Group
  private readonly surfaceMaterial: THREE.ShaderMaterial
  private readonly glowMaterial: THREE.ShaderMaterial
  private readonly ringMaterial: THREE.ShaderMaterial | null
  private readonly lightDirection: THREE.Vector3
  private readonly worldPosition: THREE.Vector3
  private angle: number
  private highlightTarget: number
  private highlightCurrent: number

  /**
   * @param {CelestialBody} body - The planet's data entry from the constants file.
   * @param {number} styleSeed - A per-planet seed offset into the surface noise.
   */
  public constructor(body: CelestialBody, styleSeed: number) {
    this.body = body
    this.angle = body.startAngle
    this.highlightTarget = 0
    this.highlightCurrent = 0
    this.lightDirection = new THREE.Vector3()
    this.worldPosition = new THREE.Vector3()

    this.object = new THREE.Group()
    this.object.rotation.set(body.orbitInclination, body.startAngle * 0.3, body.orbitInclination * 0.4)

    this.pivot = new THREE.Group()
    this.object.add(this.pivot)

    this.bodyGroup = new THREE.Group()
    this.bodyGroup.position.set(body.orbitRadius, 0, 0)
    this.pivot.add(this.bodyGroup)

    this.surfaceMaterial = new THREE.ShaderMaterial({
      vertexShader: bodyVertex,
      fragmentShader: withNoise(planetFragment),
      uniforms: {
        uTime: { value: 0 },
        uSurfaceColour: { value: new THREE.Color(body.surfaceColour) },
        uAccentColour: { value: new THREE.Color(body.accentColour) },
        uGlowColour: { value: new THREE.Color(body.glowColour) },
        uNoiseScale: { value: body.noiseScale },
        uStyleSeed: { value: styleSeed },
        uLightDirection: { value: this.lightDirection },
        uHighlight: { value: 0 },
      },
    })

    this.pickMesh = new THREE.Mesh(new THREE.SphereGeometry(body.radius, 48, 48), this.surfaceMaterial)
    this.pickMesh.userData.bodyId = body.id
    this.bodyGroup.add(this.pickMesh)

    this.glowMaterial = new THREE.ShaderMaterial({
      vertexShader: glowVertex,
      fragmentShader: glowFragment,
      transparent: true,
      side: THREE.BackSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uColour: { value: new THREE.Color(body.glowColour) },
        uIntensity: { value: 0.9 },
        uPower: { value: 3 },
      },
    })
    const glowShell = new THREE.Mesh(new THREE.SphereGeometry(body.radius * 1.2, 36, 36), this.glowMaterial)
    this.bodyGroup.add(glowShell)

    this.ringMaterial = this.buildRing()

    if (body.visualStyle === 'asteroid-belt') {
      this.addAsteroidCluster()
    }

    this.object.visible = !body.isHidden
  }

  /**
   * Advance the planet's orbit, spin, surface animation, and hover highlight.
   *
   * @param {number} elapsedSeconds - Total elapsed time since start.
   * @param {number} deltaSeconds - Time since the previous frame.
   * @returns {void}
   */
  public update(elapsedSeconds: number, deltaSeconds: number): void {
    this.angle += this.body.orbitSpeed * deltaSeconds * sceneConfig.motion.orbitTimeScale
    this.pivot.rotation.y = this.angle
    this.pickMesh.rotation.y += deltaSeconds * this.body.spinSpeed

    this.bodyGroup.getWorldPosition(this.worldPosition)
    this.lightDirection.copy(this.worldPosition).multiplyScalar(-1).normalize()

    this.highlightCurrent = damp(this.highlightCurrent, this.highlightTarget, 0.12, deltaSeconds)
    const scale = 1 + this.highlightCurrent * 0.18
    this.bodyGroup.scale.setScalar(scale)

    this.surfaceMaterial.uniforms.uTime.value = elapsedSeconds
    this.surfaceMaterial.uniforms.uHighlight.value = this.highlightCurrent
    this.glowMaterial.uniforms.uIntensity.value = 0.9 + this.highlightCurrent * 1.1
    if (this.ringMaterial !== null) {
      this.ringMaterial.uniforms.uTime.value = elapsedSeconds
    }
  }

  /**
   * Read the planet's current world-space position.
   *
   * @param {THREE.Vector3} target - The vector to write the position into.
   * @returns {THREE.Vector3} The same target vector, for chaining.
   */
  public getWorldPosition(target: THREE.Vector3): THREE.Vector3 {
    return this.bodyGroup.getWorldPosition(target)
  }

  /**
   * Set whether this planet is highlighted (hovered or focused).
   *
   * @param {boolean} isHighlighted - True to brighten and enlarge the planet.
   * @returns {void}
   */
  public setHighlighted(isHighlighted: boolean): void {
    this.highlightTarget = isHighlighted ? 1 : 0
  }

  /**
   * Reveal or hide a hidden body (used by the anomaly easter egg).
   *
   * @param {boolean} isRevealed - True to show the planet.
   * @returns {void}
   */
  public setRevealed(isRevealed: boolean): void {
    this.object.visible = isRevealed
  }

  /**
   * Build the optional ring mesh and return its material for animation.
   *
   * @returns {THREE.ShaderMaterial | null} The ring material, or null if no ring.
   */
  private buildRing(): THREE.ShaderMaterial | null {
    if (this.body.ring === null) {
      return null
    }
    const innerRadius = this.body.radius * this.body.ring.innerScale
    const outerRadius = this.body.radius * this.body.ring.outerScale
    const material = new THREE.ShaderMaterial({
      vertexShader: ringVertex,
      fragmentShader: withNoise(ringFragment),
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uColour: { value: new THREE.Color(this.body.ring.colour) },
        uInnerRadius: { value: innerRadius },
        uOuterRadius: { value: outerRadius },
        uOpacity: { value: this.body.ring.opacity },
        uTime: { value: 0 },
      },
    })
    const ring = new THREE.Mesh(new THREE.RingGeometry(innerRadius, outerRadius, 128), material)
    ring.rotation.x = -Math.PI / 2 + this.body.ring.tilt
    this.bodyGroup.add(ring)
    return material
  }

  /**
   * Scatter a flat cluster of small rocks around the body for the asteroid style.
   *
   * @returns {void}
   */
  private addAsteroidCluster(): void {
    const rockCount = 16
    const rockGeometry = new THREE.IcosahedronGeometry(this.body.radius * 0.22, 0)
    for (let index = 0; index < rockCount; index += 1) {
      const rock = new THREE.Mesh(rockGeometry, this.surfaceMaterial)
      const orbitAngle = (index / rockCount) * Math.PI * 2 + Math.random() * 0.4
      const distance = this.body.radius * (1.9 + Math.random() * 0.9)
      rock.position.set(
        Math.cos(orbitAngle) * distance,
        (Math.random() - 0.5) * this.body.radius * 0.5,
        Math.sin(orbitAngle) * distance,
      )
      rock.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI)
      rock.scale.setScalar(0.6 + Math.random() * 0.8)
      this.bodyGroup.add(rock)
    }
  }
}
